<template>
  <HeaderComponent/>
  <div class="container mx-auto p-4 h-full">
    <h1 class="text-3xl font-bold text-center mb-8">Web-to-Lead Form Builder</h1>

    <div class="flex flex-col lg:flex-row gap-8">
      <div class="w-full lg:w-1/2">
        <h2 class="text-2xl font-semibold mb-4">Form Settings</h2>

        <label class="block mb-2" for="logoOrName">Logo URL or Company Name:</label>
        <input
            v-model="logoOrName"
            type="text"
            id="logoOrName"
            placeholder="Enter logo URL or company name"
            class="w-full p-2 border rounded mb-4"
            @change="updatePreview"
        >

        <label class="block mb-2" for="fields">Select Fields <span class="text-red-500">*</span> :</label>
        <select
            v-model="selectedFields"
            id="fields"
            multiple
            class="w-full p-2 border rounded mb-4"
            @change="updatePreview"
        >
          <option value="first_name">First Name</option>
          <option value="last_name">Last Name</option>
          <option value="email">Email</option>
          <option value="phone">Phone</option>
          <option value="company">Company</option>
        </select>

        <label class="block mb-2" for="salesforceId">Salesforce Organization ID <span class="text-red-500">*</span> :
          <a href="https://arrify.com/find-your-salesforce-org-id"
             target="_blank"
             class="text-blue-500 text-sm ml-2 hover:underline">
            How to find your Org ID
          </a>
        </label>
        <input
            v-model="salesforceId"
            type="text"
            id="salesforceId"
            placeholder="Enter your Salesforce Org ID"
            class="w-full p-2 border rounded mb-4"
            :class="{ 'border-red-500': showErrors }"
            @change="updatePreview"
        >

        <label class="block mb-2" for="template">Select Template:</label>
        <select
            v-model="selectedTemplate"
            id="template"
            class="w-full p-2 border rounded mb-4"
            @change="updatePreview"
        >
          <option value="default">Default Template</option>
          <option value="minimalist">Minimalist Template</option>
          <option value="colorful">Colorful Template</option>
          <option value="professional">Professional Template</option>
          <option value="modern">Modern Template</option>
          <option value="playful">Playful Template</option>
          <option value="elegant">Elegant Template</option>
          <option value="tech">Tech Template</option>
          <option value="corporate">Corporate Template</option>
          <option value="retro">Retro Template</option>
          <option value="nature">Nature Template</option>
        </select>

        <button
            @click="generateAndDownload"
            class="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition duration-300"
        >
          Generate and Download Form
        </button>
      </div>

      <div class="w-full lg:w-1/2">
        <h2 class="text-2xl font-semibold mb-4">Preview</h2>
        <iframe
            ref="previewFrame"
            class="w-full h-[600px] border border-gray-300 rounded mt-12"
        ></iframe>
      </div>
    </div>

    <div class="max-w-4xl mx-auto">
      <!-- Header Section -->
      <div class="text-center mb-12 pt-16">
        <h1 class="text-3xl md:text-3xl font-bold mb-4 text-gray-900">
          Web-to-Lead Guide
        </h1>
        <p class="text-lg text-gray-600 max-w-2xl mx-auto">
          Follow below steps to create and customize your Web-to-Lead form using the Web-to-Lead Form Builder tool
        </p>
      </div>

      <!-- Web-to-Lead Guide-->
      <div class="space-y-6 md:space-y-8">
        <ol>
          <li>
            <span class="step-title">Step 1:</span> After selecting <strong>"Web-to-Lead Form Builder"</strong> under
            Salesforce Tools, you'll be taken to the <strong>"Web-to-Lead Form Builder"</strong> page. Here, you can
            customize your form by:
            <ul>
              <li>&#x2022; Uploading your company logo or entering your company name.</li>
              <li>&#x2022; Selecting the fields you want to include in the form.</li>
              <li>&#x2022; Choosing a template that suits your branding and needs.</li>
            </ul>
            <p class="mb-1">Additionally, you will need to provide your <strong>Salesforce Organization ID</strong>,
              which connects the form to your Salesforce account for "Lead" creation.<a
                  href="https://arrify.com/find-your-salesforce-org-id"
                  target="_blank"
                  class="text-blue-500 ml-1 hover:underline">Click here
              </a>
              to learn how to find your Salesforce Org ID.
            </p>
            <img src="@/assets/configure-web-2-lead-forms.jpeg" alt="select web-to-lead form builder">
          </li>
          <li>
            <span class="step-title">Step 2:</span> You have two options for using the Web-to-Lead form once it's
            generated:
            <ol class="list-decimal list-inside mb-1 ml-5">
              <li>
                <strong>Generate and Download the Form:</strong>
                <p class="ml-4">
                  This option allows you to download the HTML code for the Web-to-Lead form as a file. You can then
                  embed this HTML code on your website in one of two ways:
                </p>
                <ul class="list-disc list-inside">
                  <li class="ml-2"><strong>Direct Embed:</strong> Add the HTML code directly to your website where you
                    want the form to appear.
                  </li>
                  <li class="ml-2"><strong>WordPress Embed:</strong> If you're using WordPress, you can add the HTML
                    code as a shortcode, and then embed the shortcode wherever you want the form to show up on your
                    site.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Submit:</strong>
                <p>If you want to immediately create a "Lead" using the form data, select <strong>Submit</strong>. This
                  will send the form submission to Salesforce right away.</p>
              </li>
              <span class="note">
                    For this guide, we'll focus on selecting <strong>"Generate and Download Form"</strong> to save the form for later use.
                  </span>
            </ol>
            <img src="@/assets/generate-download-web-2-lead-form.png" alt="select web-to-lead form builder">
          </li>
          <li>
            <span class="step-title">Step 3:</span> Once you've embedded the <strong>Web-to-Lead</strong> form on your
            site, navigate to the form, complete the required fields, and then click Submit to send the data directly to
            Salesforce and create a new "Lead".
            <img src="@/assets/fill-web-to-lead-form.jpeg" alt="select web-to-lead form builder">
          </li>
          <li>
            <span class="step-title">Step 4:</span> After submitting the form, log in to your
            <strong>Salesforce</strong> account and navigate to the <strong>Leads</strong> tab. You should see the lead
            record created from the form submission.
            <img src="@/assets/navigate-to-lead-tab.jpeg" alt="select web-to-lead form builder">
          </li>
        </ol>
      </div>
    </div>

  </div>
  <FooterComponent/>
</template>

<script setup>
import {ref, onMounted, computed} from 'vue';
import {useRoute} from 'vue-router';
import { useWordPressMeta } from '../composables/useWordPressMeta'; // ADD THIS
import HeaderComponent from "../components/Header.vue";
import FooterComponent from "../components/Footer.vue";

// Get current route and URL
const route = useRoute();

const slug = computed(() => route.params.slug);

const currentUrl = computed(() => {
  return '/tools/salesforce-web-to-lead-form-builder';
});

const { metaData, error, isLoading } = useWordPressMeta(
    currentUrl.value,
    {
      cacheKey: 'salesforce-form-meta',
      fallbackTitle: 'X Web-to-Lead Form Builder',
    }
);


const logoOrName = ref('');
const selectedFields = ref(['first_name', 'last_name', 'email', 'phone']);
const salesforceId = ref('');
const selectedTemplate = ref('default');
const previewFrame = ref(null);
const showErrors = ref(false);

const validateSalesforceId = () => {
  if (salesforceId.value.trim() === '') {
    showErrors.value = true;
  } else {
    showErrors.value = false;
  }
};

const updatePreview = () => {
  const formHtml = generateFormHtml();
  if (previewFrame.value) {
    previewFrame.value.srcdoc = formHtml;
  }
};

const generateFormHtml = () => {
  let logoOrNameHtml = '';
  if (logoOrName.value) {
    if (logoOrName.value.toLowerCase().endsWith('.png') || logoOrName.value.toLowerCase().endsWith('.jpg') || logoOrName.value.toLowerCase().endsWith('.jpeg') || logoOrName.value.toLowerCase().endsWith('.gif')) {
      logoOrNameHtml = `<div class="logo-container"><img src="${logoOrName.value}" alt="Company Logo"></div>`;
    } else {
      logoOrNameHtml = `<div class="logo-container"><h2>${logoOrName.value}</h2></div>`;
    }
  }

  let formHtml = `
        ${logoOrNameHtml}
        <form action="https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8" method="POST">
          <input type="hidden" name="oid" value="${salesforceId.value}">
          <input type="hidden" name="retURL" value="http://www.yourwebsite.com/thankyou.html">
      `;

  const fieldLabels = {
    first_name: 'First Name',
    last_name: 'Last Name',
    email: 'Email',
    company: 'Company',
    phone: 'Phone'
  };

  selectedFields.value.forEach(field => {
    formHtml += `
          <label for="${field}">${fieldLabels[field]}</label>
          <input type="text" id="${field}" name="${field}" required><br>
        `;
  });

  formHtml += `
          <input type="submit" value="Submit">
        </form>
      `;

  const styleHtml = getTemplateStyle();

  return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Salesforce Web-to-Lead Form</title>
          ${styleHtml}
        </head>
        <body>
          <div class="form-container">
            ${formHtml}
          </div>
        </body>
        </html>
      `;
};

const getTemplateStyle = () => {
  switch (selectedTemplate.value) {
    case 'minimalist':
      return `
              <style>
              body { font-family: Arial, sans-serif; background-color: #f9f9f9; }
              .form-container { max-width: 400px; margin: 20px auto; padding: 20px; background-color: white; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
              .logo-container { text-align: center; margin-bottom: 20px; }
              .logo-container img { max-width: 200px; max-height: 100px; }
              input[type="text"] { width: 100%; padding: 8px; margin-bottom: 10px; border: 1px solid #ddd; box-sizing: border-box; }
              input[type="submit"] { background-color: #f0f0f0; border: none; padding: 10px 20px; cursor: pointer; width: 100%; }
              </style>
          `;
    case 'colorful':
      return `
              <style>
              body { font-family: 'Trebuchet MS', sans-serif; background-color: #f0f8ff; }
              .form-container { max-width: 500px; margin: 20px auto; padding: 20px; border-radius: 10px; background-color: white; box-shadow: 0 0 20px rgba(0,0,0,0.1); }
              .logo-container { text-align: center; margin-bottom: 20px; }
              .logo-container img { max-width: 200px; max-height: 100px; }
              input[type="text"] { width: 100%; padding: 10px; margin-bottom: 15px; border: 2px solid #4682b4; border-radius: 5px; box-sizing: border-box; }
              input[type="submit"] { background-color: #4682b4; color: white; border: none; padding: 12px 24px; cursor: pointer; border-radius: 5px; width: 100%; }
              </style>
          `;
    case 'professional':
      return `
              <style>
              body { font-family: 'Arial', sans-serif; background-color: #f5f5f5; }
              .form-container { max-width: 500px; margin: 20px auto; padding: 30px; background-color: white; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
              .logo-container { text-align: center; margin-bottom: 20px; }
              .logo-container img { max-width: 200px; max-height: 100px; }
              label { font-weight: bold; color: #333; }
              input[type="text"] { width: 100%; padding: 10px; margin: 8px 0 20px; border: 1px solid #ccc; box-sizing: border-box; }
              input[type="submit"] { background-color: #0056b3; color: white; border: none; padding: 12px 24px; cursor: pointer; width: 100%; font-size: 16px; }
              </style>
          `;
    case 'modern':
      return `
              <style>
              body { font-family: 'Roboto', sans-serif; background-color: #ffffff; }
              .form-container { max-width: 450px; margin: 20px auto; padding: 30px; background-color: #f8f9fa; border-radius: 8px; }
              .logo-container { text-align: center; margin-bottom: 20px; }
              .logo-container img { max-width: 200px; max-height: 100px; }
              label { font-weight: 500; color: #495057; }
              input[type="text"] { width: 100%; padding: 12px; margin: 8px 0 20px; border: none; border-bottom: 2px solid #ced4da; background-color: transparent; transition: border-color 0.3s; }
              input[type="text"]:focus { outline: none; border-color: #007bff; }
              input[type="submit"] { background-color: #007bff; color: white; border: none; padding: 12px 24px; cursor: pointer; width: 100%; font-size: 16px; border-radius: 4px; transition: background-color 0.3s; }
              input[type="submit"]:hover { background-color: #0056b3; }
              </style>
          `;
    case 'playful':
      return `
              <style>
              body { font-family: 'Comic Sans MS', cursive; background-color: #ffd700; }
              .form-container { max-width: 500px; margin: 20px auto; padding: 30px; background-color: #ffffff; border-radius: 20px; box-shadow: 0 0 20px rgba(0,0,0,0.2); }
              .logo-container { text-align: center; margin-bottom: 20px; }
              .logo-container img { max-width: 200px; max-height: 100px; }
              label { font-weight: bold; color: #ff6b6b; }
              input[type="text"] { width: 100%; padding: 10px; margin: 8px 0 20px; border: 3px solid #4ecdc4; border-radius: 10px; box-sizing: border-box; }
              input[type="submit"] { background-color: #ff6b6b; color: white; border: none; padding: 12px 24px; cursor: pointer; width: 100%; font-size: 18px; border-radius: 10px; transition: transform 0.3s; }
              input[type="submit"]:hover { transform: scale(1.05); }
              </style>
          `;
    case 'elegant':
      return `
              <style>
              body { font-family: 'Garamond', serif; background-color: #f9f7f1; color: #333; }
              .form-container { max-width: 500px; margin: 20px auto; padding: 40px; background-color: #fff; box-shadow: 0 0 20px rgba(0,0,0,0.1); }
              .logo-container { text-align: center; margin-bottom: 20px; }
              .logo-container img { max-width: 200px; max-height: 100px; }
              label { font-weight: normal; color: #5d5d5d; letter-spacing: 1px; }
              input[type="text"] { width: 100%; padding: 10px; margin: 8px 0 20px; border: none; border-bottom: 1px solid #ccc; background-color: transparent; transition: border-color 0.3s; }
              input[type="text"]:focus { outline: none; border-color: #9b8a61; }
              input[type="submit"] { background-color: #9b8a61; color: white; border: none; padding: 12px 24px; cursor: pointer; width: 100%; font-size: 16px; letter-spacing: 2px; transition: background-color 0.3s; }
              input[type="submit"]:hover { background-color: #7d6f4e; }
              </style>
          `;
    case 'tech':
      return `
              <style>
              body { font-family: 'Courier New', monospace; background-color: #0a192f; color: #64ffda; }
              .form-container { max-width: 500px; margin: 20px auto; padding: 30px; background-color: #112240; border: 1px solid #64ffda; }
              .logo-container { text-align: center; margin-bottom: 20px; }
              .logo-container img { max-width: 200px; max-height: 100px; }
              label { font-weight: bold; color: #64ffda; }
              input[type="text"] { width: 100%; padding: 10px; margin: 8px 0 20px; border: 1px solid #64ffda; background-color: transparent; color: #64ffda; box-sizing: border-box; }
              input[type="text"]:focus { outline: none; border-color: #ff79c6; }
              input[type="submit"] { background-color: transparent; color: #64ffda; border: 1px solid #64ffda; padding: 12px 24px; cursor: pointer; width: 100%; font-size: 16px; transition: all 0.3s; }
              input[type="submit"]:hover { background-color: rgba(100, 255, 218, 0.1); }
              </style>
          `;
    case 'corporate':
      return `
              <style>
              body { font-family: 'Arial', sans-serif; background-color: #f0f0f0; color: #333; }
              .form-container { max-width: 550px; margin: 20px auto; padding: 40px; background-color: #fff; box-shadow: 0 0 20px rgba(0,0,0,0.1); }
              .logo-container { text-align: center; margin-bottom: 20px; }
              .logo-container img { max-width: 200px; max-height: 100px; }
              label { font-weight: bold; color: #2c3e50; }
              input[type="text"] { width: 100%; padding: 12px; margin: 8px 0 20px; border: 1px solid #bdc3c7; border-radius: 4px; box-sizing: border-box; }
              input[type="submit"] { background-color: #3498db; color: white; border: none; padding: 14px 28px; cursor: pointer; width: 100%; font-size: 16px; border-radius: 4px; transition: background-color 0.3s; }
              input[type="submit"]:hover { background-color: #2980b9; }
              </style>
          `;
    case 'retro':
      return `
              <style>
              body { font-family: 'Courier New', monospace; background-color: #f5e5d3; color: #8b4513; }
              .form-container { max-width: 500px; margin: 20px auto; padding: 30px; background-color: #fffaf0; border: 3px solid #8b4513; }
              .logo-container { text-align: center; margin-bottom: 20px; }
              .logo-container img { max-width: 200px; max-height: 100px; }
              label { font-weight: bold; color: #8b4513; }
              input[type="text"] { width: 100%; padding: 10px; margin: 8px 0 20px; border: 2px solid #8b4513; background-color: #fff8dc; color: #8b4513; box-sizing: border-box; }
              input[type="submit"] { background-color: #cd853f; color: #ffffff; border: none; padding: 12px 24px; cursor: pointer; width: 100%; font-size: 16px; transition: all 0.3s; }
              input[type="submit"]:hover { background-color: #8b4513; }
              </style>
          `;
    case 'nature':
      return `
              <style>
              body { font-family: 'Verdana', sans-serif; background-color: #e8f5e9; color: #1b5e20; }
              .form-container { max-width: 500px; margin: 20px auto; padding: 30px; background-color: #ffffff; border-radius: 15px; box-shadow: 0 0 20px rgba(76, 175, 80, 0.2); }
              .logo-container { text-align: center; margin-bottom: 20px; }
              .logo-container img { max-width: 200px; max-height: 100px; }
              label { font-weight: normal; color: #2e7d32; }
              input[type="text"] { width: 100%; padding: 10px; margin: 8px 0 20px; border: 1px solid #4caf50; border-radius: 5px; background-color: #f1f8e9; color: #1b5e20; box-sizing: border-box; }
              input[type="submit"] { background-color: #4caf50; color: white; border: none; padding: 12px 24px; cursor: pointer; width: 100%; font-size: 16px; border-radius: 5px; transition: background-color 0.3s; }
              input[type="submit"]:hover { background-color: #45a049; }
              </style>
          `;
    default:
      return `
              <style>
              body { font-family: Helvetica, sans-serif; }
              .form-container { max-width: 450px; margin: 20px auto; }
              .logo-container { text-align: center; margin-bottom: 20px; }
              .logo-container img { max-width: 200px; max-height: 100px; }
              input[type="text"] { width: 100%; padding: 10px; margin-bottom: 12px; border: 1px solid #ccc; box-sizing: border-box; }
              input[type="submit"] { background-color: #0070d2; color: white; border: none; padding: 10px 20px; cursor: pointer; width: 100%; }
              </style>
          `;
  }
};

const generateAndDownload = () => {
  validateSalesforceId();
  if (showErrors.value == false) {
    const formHtml = generateFormHtml();
    const blob = new Blob([formHtml], {type: 'text/html'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'web_to_lead_form.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } else {
    console.log('Not valid to submit : ' + showErrors.value);
  }
};

onMounted(() => {
  updatePreview();
});
</script>

<style scoped>
li {
  margin-bottom: 5px;
}

ul {
  margin-left: 20px;
}

.step-title {
  font-weight: bold;
  color: #0073e6;
}

.note {
  font-style: italic;
  color: #555;
}
</style>